import {
	IWhatsAppTemplateEditorValue,
	TWhatsAppTemplateButtonType,
	TWhatsAppTemplateSampleMedia,
} from "./types"
import { validateTemplate } from "./template-parser"
import type { TTemplateVariable } from "../../Integrations"

export type TTemplateVariableContext = {
	name: string
	available_parameters: TTemplateVariable[]
	/**
	 * Optional wildcard variable prefix accepted by the source (e.g.
	 * "extras_" for TripPlanRequest). Variables matching the prefix are
	 * treated as valid even when not in `available_parameters` — backend
	 * resolves them from a JSON column at send time.
	 */
	wildcard_prefix?: string | null
}

export const HEADER_TYPE_OPTIONS = [
	{ id: "none", label: "None", value: "none" },
	{ id: "text", label: "Text", value: "text" },
	{ id: "image", label: "Image", value: "image" },
	{ id: "video", label: "Video", value: "video" },
	{ id: "document", label: "Document", value: "document" },
]

export const MEDIA_MODE_OPTIONS = [
	{ id: "static", label: "Static Media", value: "static" },
	{ id: "dynamic", label: "Dynamic Media", value: "dynamic" },
]

export const CATEGORY_OPTIONS = [
	{ id: "UTILITY" as const, label: "Utility", value: "UTILITY" },
	{ id: "MARKETING" as const, label: "Marketing", value: "MARKETING" },
	{
		id: "AUTHENTICATION" as const,
		label: "Authentication",
		value: "AUTHENTICATION",
	},
]

export const BUTTON_TYPE_OPTIONS = [
	{ id: "quick_reply", label: "Quick Reply", value: "quick_reply" },
	{ id: "url", label: "Call To Action URL", value: "url" },
	{ id: "phone_number", label: "Call To Action Phone", value: "phone_number" },
	{ id: "copy_code", label: "Copy Code", value: "copy_code" },
	{ id: "flow", label: "Open a Form (Flow)", value: "flow" },
]

/**
 * What a tap on a quick reply can do besides reaching the automations that
 * answer taps. Mirrors the API's button-action registry, which validates it.
 */
export const QUICK_REPLY_ACTION_OPTIONS = [
	{ id: "", label: "Nothing more", value: "" },
	{
		id: "trip_day_plan",
		label: "Send the trip day plan",
		value: "trip_day_plan",
	},
]

export const AUTHENTICATION_BUTTON_TYPE_OPTIONS = BUTTON_TYPE_OPTIONS.filter(
	(option) => option.value === "copy_code"
)

export const AUTHENTICATION_DURATION_OPTIONS = [
	{ id: "1", label: "1 minute", value: 1 },
	{ id: "2", label: "2 minutes", value: 2 },
	{ id: "3", label: "3 minutes", value: 3 },
	{ id: "5", label: "5 minutes", value: 5 },
	{ id: "10", label: "10 minutes", value: 10 },
	{ id: "15", label: "15 minutes", value: 15 },
	{ id: "30", label: "30 minutes", value: 30 },
	{ id: "60", label: "60 minutes", value: 60 },
	{ id: "90", label: "90 minutes", value: 90 },
]

export function emptyButton(type: TWhatsAppTemplateButtonType = "quick_reply") {
	return {
		type,
		text: type === "copy_code" ? "Copy code" : "",
		url: "",
		phone_number: "",
		code: "",
		flow_id: "",
		flow_screen: "",
	}
}

export function createAuthenticationEditorValue(
	value: IWhatsAppTemplateEditorValue
): IWhatsAppTemplateEditorValue {
	return {
		...value,
		automation_template_key: null,
		category: "AUTHENTICATION",
		body: {
			text: "<VERIFICATION_CODE> is your verification code.",
		},
		header: {
			type: "none",
			text: "",
			registration_handle: "",
			static_image_url: "",
			image_mode: "static",
			dynamic_image_source_key: "",
			sample_media_id: null,
			sample_media: null,
		},
		footer: {
			enabled: false,
			text: "",
		},
		buttons: [emptyButton("copy_code")],
		authentication: {
			security_recommendation: true,
			code_expiration_enabled:
				value.authentication.code_expiration_enabled || false,
			code_expiration_minutes:
				value.authentication.code_expiration_minutes || 5,
			message_validity_enabled:
				value.authentication.message_validity_enabled || false,
			message_validity_seconds:
				value.authentication.message_validity_seconds || 600,
		},
	}
}

export function createGenericEditorValue(
	value: IWhatsAppTemplateEditorValue,
	category: "UTILITY" | "MARKETING"
): IWhatsAppTemplateEditorValue {
	return {
		...value,
		category,
		automation_template_key: null,
		body:
			value.body.text === "<VERIFICATION_CODE> is your verification code."
				? { text: "" }
				: value.body,
		buttons: value.buttons.filter((button) => button.type !== "copy_code"),
		authentication: {
			security_recommendation: false,
			code_expiration_enabled: false,
			code_expiration_minutes: 5,
			message_validity_enabled: false,
			message_validity_seconds: 600,
		},
	}
}

export function normalizeEditorForCategoryTransition(
	nextValue: IWhatsAppTemplateEditorValue,
	previousValue: IWhatsAppTemplateEditorValue
): IWhatsAppTemplateEditorValue {
	if (
		nextValue.category === previousValue.category ||
		nextValue.category === "AUTHENTICATION"
	) {
		return nextValue.category === "AUTHENTICATION"
			? createAuthenticationEditorValue(nextValue)
			: nextValue
	}

	return createGenericEditorValue(nextValue, nextValue.category)
}

export function updateEditorValue(
	value: IWhatsAppTemplateEditorValue,
	path: string[],
	newValue: unknown
): IWhatsAppTemplateEditorValue {
	const cloneValue = (input: unknown): unknown => {
		if (input instanceof File) {
			return input
		}

		if (Array.isArray(input)) {
			return input.map(cloneValue)
		}

		if (input && typeof input === "object") {
			return Object.fromEntries(
				Object.entries(input).map(([key, current]) => [
					key,
					cloneValue(current),
				])
			)
		}

		return input
	}

	const nextValue = cloneValue(value) as IWhatsAppTemplateEditorValue
	let cursor: Record<string, unknown> = nextValue as never as Record<
		string,
		unknown
	>

	path.slice(0, -1).forEach((segment) => {
		const current = cursor[segment]
		if (!current || typeof current !== "object") {
			cursor[segment] = {}
		}
		cursor = cursor[segment] as Record<string, unknown>
	})

	cursor[path[path.length - 1]] = newValue

	return nextValue
}

export function createSampleMediaSelection(
	file: File,
	current?: TWhatsAppTemplateSampleMedia | null
): TWhatsAppTemplateSampleMedia {
	if (current?.preview_url?.startsWith("blob:")) {
		URL.revokeObjectURL(current.preview_url)
	}

	return {
		id: current?.id || null,
		url: current?.url || null,
		mime_type: file.type,
		file_name: file.name,
		name: file.name,
		file,
		preview_url: URL.createObjectURL(file),
	}
}

export function buildVariableErrors(
	variableContext: TTemplateVariableContext,
	value: IWhatsAppTemplateEditorValue
) {
	const wildcardPrefix = variableContext.wildcard_prefix
	const bodyValidation = validateTemplate(
		value.body.text || "",
		variableContext.available_parameters,
		wildcardPrefix
	)
	const headerValidation =
		value.header.type === "text" && value.header.text
			? validateTemplate(
					value.header.text,
					variableContext.available_parameters,
					wildcardPrefix
				)
			: { valid: true, invalidVariables: [], errors: [] as string[] }
	const footerValidation =
		value.footer.enabled && value.footer.text
			? validateTemplate(
					value.footer.text,
					variableContext.available_parameters,
					wildcardPrefix
				)
			: { valid: true, invalidVariables: [], errors: [] as string[] }

	return {
		bodyValidation,
		headerValidation,
		footerValidation,
		hasErrors:
			!bodyValidation.valid ||
			!headerValidation.valid ||
			!footerValidation.valid,
		invalidVariables: [
			...bodyValidation.invalidVariables,
			...headerValidation.invalidVariables,
			...footerValidation.invalidVariables,
		],
	}
}
